import { ReactNode, useEffect, useRef } from 'react';

interface MorphVisualProps {
  children: ReactNode;
  className?: string;
  maxRadius?: number;
  maxScale?: number;
}

/**
 * 大きなビジュアル用：スクロールに連動して
 * 縮小（scale）しながら角丸（border-radius）が 0 → 大きく 付いていく。
 */
export default function MorphVisual({
  children,
  className = '',
  maxRadius = 40,
  maxScale = 1.15,
}: MorphVisualProps) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    let raf = 0;

    const update = () => {
      raf = requestAnimationFrame(() => {
        const rect = outer.getBoundingClientRect();
        const vh = window.innerHeight;
        const start = vh;
        const end = vh * 0.12;
        const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));

        const scale = maxScale - progress * (maxScale - 1);
        const radius = progress * maxRadius;

        outer.style.borderRadius = `${radius}px`;
        inner.style.transform = `scale(${scale})`;
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [maxRadius, maxScale]);

  return (
    <div ref={outerRef} className={`overflow-hidden ${className}`} style={{ borderRadius: '0px' }}>
      <div
        ref={innerRef}
        style={{
          transform: `scale(${maxScale})`,
          transformOrigin: 'center center',
          willChange: 'transform',
        }}
      >
        {children}
      </div>
    </div>
  );
}